import { useState, useEffect } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Pin, Edit, Check, X, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { supabase } from "@/integrations/supabase/client"
import { useIsAdmin } from "@/hooks/useUserRole"
import { useToast } from "@/hooks/use-toast"
import { RuleContentRenderer } from "./RuleContentRenderer"

export type RuleType = "feed" | "introductions" | "questions" | "learning" | "general"

interface PinnedRuleProps {
  content?: string;
  type: RuleType;
}

export function PinnedRule({ content: fallbackContent, type }: PinnedRuleProps) {
  const [rule, setRule] = useState("")
  const [isEditing, setIsEditing] = useState(false)
  const [editedRule, setEditedRule] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const { isAdmin } = useIsAdmin()
  const { toast } = useToast()

  useEffect(() => {
    const fetchRule = async () => {
      const { data, error } = await (supabase
        .from('community_pinned_rules') as any)
        .select('content')
        .eq('is_active', true)
        .eq('rule_type', type)
        .order('created_at', { ascending: false })
        .limit(1)

      if (error) {
        console.error('Error fetching rule:', error)
      }

      if (data && data.length > 0) {
        setRule(data[0].content)
      } else if (fallbackContent) {
        setRule(fallbackContent)
      }
      setLoaded(true)
    }

    fetchRule()
  }, [type, fallbackContent])

  const handleEdit = () => {
    setEditedRule(rule)
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
    setEditedRule("")
  }

  const handleSave = async () => {
    if (!editedRule.trim()) return

    try {
      setIsSaving(true)

      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error("Usuário não autenticado")

      // Deactivate previous rules of same type
      await (supabase
        .from('community_pinned_rules') as any)
        .update({ is_active: false })
        .eq('is_active', true)
        .eq('rule_type', type)

      const { error } = await (supabase
        .from('community_pinned_rules') as any)
        .insert([{ content: editedRule, created_by: user.id, is_active: true, rule_type: type }])

      if (error) throw error

      setRule(editedRule)
      setIsEditing(false)
      toast({ title: "Regra atualizada com sucesso!" })
    } catch (error) {
      console.error('Error updating rule:', error)
      toast({ title: "Erro ao atualizar regra", variant: "destructive" })
    } finally {
      setIsSaving(false)
    }
  }

  if (!loaded || (!rule && !isEditing)) return null

  return (
    <Card className="mb-6 bg-blue-50 border-blue-200">
      <CardContent className="p-4">
        <div className="flex items-start space-x-3">
          <Pin className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 mb-2">Regras da Comunidade</h3>
            {isEditing ? (
              <div className="space-y-2">
                <Textarea
                  value={editedRule}
                  onChange={(e) => setEditedRule(e.target.value)}
                  className="min-h-[120px] bg-white font-mono text-sm"
                  placeholder={"Use formatação:\n**Título em negrito**\n- Item de lista\n\nNovo parágrafo com linha em branco"}
                />
                <p className="text-xs text-blue-600">
                  Dica: Use **texto** para negrito, - para listas, e linha em branco para separar parágrafos.
                </p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSave} disabled={!editedRule.trim() || isSaving}>
                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                    Salvar
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancel} disabled={isSaving}>
                    <X className="h-4 w-4 mr-1" />
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <RuleContentRenderer content={rule} />
            )}
          </div>
          {isAdmin && !isEditing && (
            <Button variant="ghost" size="icon" className="text-blue-600" onClick={handleEdit}>
              <Edit className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
